import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { useNavigate } from 'react-router-dom';

const DispatchJangad = () => {
  const navigate = useNavigate();
  const MAX_ROWS = 50; 
  
  // Data State
  const [parties, setParties] = useState([]);
  const [processes, setProcesses] = useState([]); 
  const [nextMemo, setNextMemo] = useState('');
  const [loading, setLoading] = useState(true);
  
  // View State
  const [viewMode, setViewMode] = useState('form'); 
  const [historyList, setHistoryList] = useState([]);
  const [historySearch, setHistorySearch] = useState('');
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    receiverId: '', 
    receiverName: '', 
    receiverAddress: '', 
    receiverGst: ''
  });

  const [rows, setRows] = useState([
    { description: '', jangadNo: '', pieces: '', carats: '', process: '' }
  ]);

  const columns = ['description', 'jangadNo', 'pieces', 'carats', 'process'];

  useEffect(() => { fetchInitialData(); }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
        const [resParties, resProc, resMemo] = await Promise.all([
            api.get('/factories'), 
            api.get('/processes'),
            api.get('/jangad/next-number')
        ]);
        setParties(resParties.data || []);
        setProcesses(resProc.data || []);
        if (!editId) setNextMemo(resMemo.data?.formatted || 'M-Error');
    } catch (error) { console.error("Load Error:", error); }
    setLoading(false);
  };

  const fetchHistory = async () => {
      try {
          const res = await api.get('/jangad/list');
          setHistoryList(res.data);
      } catch (err) { alert("Error loading history: " + err.message); }
  };

  const monthlyReport = useMemo(() => {
      const report = {};
      historyList.forEach(jangad => {
          const dateObj = new Date(jangad.date);
          const monthKey = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });
          if (!report[monthKey]) report[monthKey] = { totalPcs: 0, totalCts: 0, processes: {} };

          if(jangad.items && Array.isArray(jangad.items)) {
              jangad.items.forEach(item => {
                  const proc = item.process || 'Other';
                  const pcs = parseFloat(item.pieces) || 0;
                  const cts = parseFloat(item.carats) || 0;
                  report[monthKey].totalPcs += pcs;
                  report[monthKey].totalCts += cts;
                  if (!report[monthKey].processes[proc]) report[monthKey].processes[proc] = { pcs: 0, cts: 0 };
                  report[monthKey].processes[proc].pcs += pcs;
                  report[monthKey].processes[proc].cts += cts;
              });
          }
      });
      return report;
  }, [historyList]);

  // --- ACTIONS ---
  const toggleHistory = () => { if (viewMode !== 'history') { fetchHistory(); setViewMode('history'); } else { resetForm(); } };
  const toggleReport = () => { if (viewMode !== 'report') { fetchHistory(); setViewMode('report'); } else { resetForm(); } };

  const resetForm = () => {
      setViewMode('form');
      setEditId(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        receiverId: '', receiverName: '', receiverAddress: '', receiverGst: ''
      });
      setRows([{ description: '', jangadNo: '', pieces: '', carats: '', process: '' }]);
      api.get('/jangad/next-number').then(res => setNextMemo(res.data?.formatted));
  };

  const handleEdit = (jangad) => {
      setEditId(jangad._id);
      setNextMemo(`M-${jangad.memoNumber}`);
      setFormData({
          date: new Date(jangad.date).toISOString().split('T')[0],
          receiverId: '', 
          receiverName: jangad.receiver,
          receiverAddress: jangad.receiverAddress,
          receiverGst: jangad.gstin
      });
      const mappedRows = (jangad.items || []).map(i => ({
          description: i.description || '',
          jangadNo: i.jangadNo || '',
          pieces: i.pieces || '',
          carats: i.carats || '',
          process: i.process || ''
      }));
      setRows(mappedRows.length > 0 ? mappedRows : [{ description: '', jangadNo: '', pieces: '', carats: '', process: '' }]);
      setViewMode('form');
  };

  const handleDelete = async (id, memoNo) => {
      if(!window.confirm(`Delete Memo M-${memoNo}?`)) return;
      try { await api.delete(`/jangad/delete/${id}`); alert("Deleted."); fetchHistory(); } 
      catch (err) { alert("Error: " + err.message); }
  };

  const handlePartyChange = (e) => {
    const pId = e.target.value;
    const selected = parties.find(p => p._id === pId);
    if (selected) {
        setFormData({ 
            ...formData, 
            receiverId: pId, 
            receiverName: selected.companyName || selected.name, 
            receiverAddress: selected.address || '', 
            receiverGst: selected.gstNumber || '' 
        });
    } else {
        setFormData({ ...formData, receiverId: '', receiverName: '', receiverAddress: '', receiverGst: '' });
    }
  };

  const handleRowChange = (index, field, value) => {
    const newRows = [...rows];
    newRows[index][field] = value;
    setRows(newRows);
  };

  const addRow = () => {
    if (rows.length >= MAX_ROWS) return;
    const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
    setRows(prev => [...prev, { description: '', jangadNo: lastRow ? lastRow.jangadNo : '', pieces: '', carats: '', process: lastRow ? lastRow.process : '' }]);
  };

  const removeRow = (index) => {
    if (rows.length > 1) setRows(rows.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e, index, field) => {
      const colIndex = columns.indexOf(field);
      if (e.key === 'Enter') {
          e.preventDefault();
          if (index === rows.length - 1) {
              addRow();
              setTimeout(() => { document.getElementById(`cell-${index + 1}-0`)?.focus(); }, 50);
          } else {
              document.getElementById(`cell-${index + 1}-${colIndex}`)?.focus();
          }
      }
  };

  const handleSaveAndPrint = async () => {
      if (!formData.receiverName) { alert("Select Receiver"); return; }
      const payload = {
          date: formData.date,
          receiver: formData.receiverName,
          receiverAddress: formData.receiverAddress,
          gstin: formData.receiverGst,
          items: rows
      };
      try {
          if (editId) { await api.put(`/jangad/update/${editId}`, payload); } 
          else { await api.post('/jangad/create', payload); }
          
          document.title = `Jangad_${nextMemo}`;
          window.print();
          
          setTimeout(() => { 
              document.title = "DiamondSys ERP"; 
              window.location.reload(); 
          }, 1000);

      } catch (err) { alert("Error: " + err.message); }
  };

  const calculateTotals = () => {
      const processMap = {};
      let totalPcs = 0, totalCts = 0;
      rows.forEach(r => {
          const pName = r.process || 'Other';
          const p = parseFloat(r.pieces) || 0;
          const c = parseFloat(r.carats) || 0;
          totalPcs += p; totalCts += c;
          
          if (!processMap[pName]) processMap[pName] = { pcs: 0, cts: 0 };
          processMap[pName].pcs += p;
          processMap[pName].cts += c;
      });
      return { totalPcs, totalCts, processBreakdown: processMap };
  };

  const { totalPcs, totalCts, processBreakdown } = calculateTotals();
  const filteredHistory = historyList.filter(h => (h.receiver || '').toLowerCase().includes(historySearch.toLowerCase()) || String(h.memoNumber).includes(historySearch));

  if (loading) return <div className="p-5 text-center text-muted">Loading...</div>;

  return (
    <div className="container mt-4 mb-5">
      
      {/* --- SCREEN VIEW --- */}
      <div className="d-print-none">
          <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                  <h4 className="fw-bold mb-0">
                      {viewMode === 'form' ? (editId ? 'Edit Jangad' : 'New Dispatch Jangad') : 
                       viewMode === 'report' ? 'Monthly Summary' : 'Dispatch History'}
                  </h4>
              </div>
              <div className="d-flex gap-2">
                  <button className={`btn btn-sm ${viewMode === 'report' ? 'btn-success' : 'btn-outline-success'}`} onClick={toggleReport}>📊 Report</button>
                  <button className={`btn btn-sm ${viewMode === 'history' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={toggleHistory}>📜 History</button>
                  {viewMode !== 'form' && <button className="btn btn-sm btn-outline-dark" onClick={resetForm}>➕ New</button>}
              </div>
          </div>

          {/* REPORT VIEW */}
          {viewMode === 'report' && (
              <div className="card shadow-sm border-success">
                  <div className="card-header bg-success text-white fw-bold">Monthly Process Breakdown</div>
                  <div className="card-body">
                      {Object.keys(monthlyReport).length === 0 ? <p className="text-center my-4">No data.</p> : (
                          <div className="accordion" id="reportAccordion">
                              {Object.entries(monthlyReport).map(([month, data], index) => (
                                  <div className="accordion-item" key={month}>
                                      <h2 className="accordion-header" id={`head${index}`}>
                                          <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target={`#collapse${index}`}>
                                              <span className="fw-bold me-3">{month}</span>
                                              <span className="badge bg-primary me-2">{data.totalPcs} Pcs</span>
                                              <span className="badge bg-success">{(data.totalCts || 0).toFixed(2)} Cts</span>
                                          </button>
                                      </h2>
                                      <div id={`collapse${index}`} className="accordion-collapse collapse" data-bs-parent="#reportAccordion">
                                          <div className="accordion-body p-0">
                                              <table className="table table-sm table-striped mb-0">
                                                  <thead className="table-light"><tr><th className="ps-4">Process</th><th className="text-center">Pieces</th><th className="text-center">Carats</th></tr></thead>
                                                  <tbody>
                                                      {Object.entries(data.processes).map(([proc, stats]) => (
                                                          <tr key={proc}><td className="ps-4">{proc}</td><td className="text-center">{stats.pcs}</td><td className="text-center fw-bold">{(stats.cts || 0).toFixed(2)}</td></tr>
                                                      ))}
                                                  </tbody>
                                              </table>
                                          </div>
                                      </div>
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>
          )}

          {/* HISTORY VIEW */}
          {viewMode === 'history' && (
              <div className="card shadow-sm">
                  <div className="card-header bg-light p-3">
                      <input type="text" className="form-control" placeholder="Search..." value={historySearch} onChange={(e) => setHistorySearch(e.target.value)} />
                  </div>
                  <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0 text-center">
                          <thead className="table-light"><tr><th>Date</th><th>Memo</th><th>Receiver</th><th>Pcs</th><th>Cts</th><th>Action</th></tr></thead>
                          <tbody>
                              {filteredHistory.length === 0 ? <tr><td colSpan="6" className="text-muted p-4">No records.</td></tr> : 
                                  filteredHistory.map(item => (
                                      <tr key={item._id}>
                                          <td>{new Date(item.date).toLocaleDateString()}</td>
                                          <td className="fw-bold text-primary">M-{item.memoNumber}</td>
                                          <td>{item.receiver}</td>
                                          <td>{item.totalPcs}</td>
                                          <td className="fw-bold">{(item.totalCarats || 0).toFixed(2)}</td>
                                          <td>
                                              <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(item)}>Edit/Print</button>
                                              <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(item._id, item.memoNumber)}>🗑️</button>
                                          </td>
                                      </tr>
                                  ))
                              }
                          </tbody>
                      </table>
                  </div>
              </div>
          )}

          {/* FORM VIEW */}
          {viewMode === 'form' && (
              <div className={`card shadow-sm p-4 ${editId ? 'border-warning' : 'border-primary'}`}>
                  <div className="row g-3 mb-4">
                      <div className="col-md-2"><label className="fw-bold small">Memo No</label><input className="form-control form-control-sm bg-light fw-bold" value={nextMemo} readOnly /></div>
                      <div className="col-md-2"><label className="small">Date</label><input type="date" className="form-control form-control-sm" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
                      <div className="col-md-4">
                          <label className="small">Receiver (Factory)</label>
                          <select className="form-select form-select-sm" onChange={handlePartyChange} value={formData.receiverId}>
                              <option value="">-- Select --</option>
                              {parties.map(p => <option key={p._id} value={p._id}>{p.companyName}</option>)}
                          </select>
                      </div>
                      <div className="col-md-4"><label className="small">Address</label><input className="form-control form-control-sm" value={formData.receiverAddress} readOnly /></div>
                  </div>

                  <table className="table table-bordered align-middle text-center table-sm">
                      <thead className="table-light">
                          <tr><th width="5%">No.</th><th width="25%">Desc</th><th width="15%">Jangad No</th><th width="10%">Pcs</th><th width="10%">Cts</th><th width="25%">Process</th><th width="5%"></th></tr>
                      </thead>
                      <tbody>
                          {rows.map((row, index) => (
                              <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td><input id={`cell-${index}-0`} className="form-control form-control-sm" value={row.description} onChange={(e) => handleRowChange(index, 'description', e.target.value)} onKeyDown={(e) => handleKeyDown(e, index, 'description')} /></td>
                                  <td><input id={`cell-${index}-1`} className="form-control form-control-sm" value={row.jangadNo} onChange={(e) => handleRowChange(index, 'jangadNo', e.target.value)} onKeyDown={(e) => handleKeyDown(e, index, 'jangadNo')} /></td>
                                  <td><input id={`cell-${index}-2`} type="number" className="form-control form-control-sm" value={row.pieces} onChange={(e) => handleRowChange(index, 'pieces', e.target.value)} onKeyDown={(e) => handleKeyDown(e, index, 'pieces')} /></td>
                                  <td><input id={`cell-${index}-3`} type="number" className="form-control form-control-sm" value={row.carats} onChange={(e) => handleRowChange(index, 'carats', e.target.value)} onKeyDown={(e) => handleKeyDown(e, index, 'carats')} /></td>
                                  <td>
                                      <select id={`cell-${index}-4`} className="form-select form-select-sm" value={row.process} onChange={(e) => handleRowChange(index, 'process', e.target.value)} onKeyDown={(e) => handleKeyDown(e, index, 'process')}>
                                          <option value="">-- Select --</option>
                                          {processes.map(p => <option key={p._id} value={p.name}>{p.name}</option>)}
                                      </select>
                                  </td>
                                  <td><button className="btn btn-sm btn-danger py-0" onClick={() => removeRow(index)} tabIndex="-1">x</button></td>
                              </tr>
                          ))}
                      </tbody>
                      <tfoot>
                          <tr className="fw-bold bg-light">
                              <td colSpan="3" className="text-end">TOTAL:</td>
                              <td className="text-primary">{totalPcs}</td>
                              <td className="text-primary">{(totalCts || 0).toFixed(2)}</td>
                              <td colSpan="2"></td>
                          </tr>
                      </tfoot>
                  </table>

                  <div className="d-flex justify-content-between mt-3">
                      <button className="btn btn-secondary btn-sm" onClick={addRow} disabled={rows.length >= MAX_ROWS}>+ Add Row</button>
                      <button className="btn btn-success px-5" onClick={handleSaveAndPrint}>{editId ? 'Update' : 'Save'} & Print</button>
                  </div>
              </div>
          )}
      </div>

      {/* --- PRINT VIEW (FIXED & POLISHED) --- */}
      <div className="d-none d-print-block print-container">
          <style>
            {`
                @media print {
                    @page { size: A5 portrait; margin: 8mm; }
                    
                    body { 
                        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; 
                        font-size: 9pt; 
                        color: #000;
                        -webkit-print-color-adjust: exact; 
                    }
                    
                    .d-print-none { display: none !important; }
                    .d-print-block { display: block !important; }
                    
                    /* CONTAINER */
                    .print-container { width: 100%; max-width: 100%; margin: 0 auto; }

                    /* HEADER */
                    .print-header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 5px; margin-bottom: 10px; }
                    .print-title { font-size: 16pt; font-weight: 800; letter-spacing: 1px; margin: 0; }
                    .print-subtitle { font-size: 8pt; font-style: italic; margin-bottom: 3px; }
                    .print-tag { background: #000; color: #fff; padding: 2px 8px; font-weight: bold; font-size: 9pt; display: inline-block; border-radius: 2px; }
                    
                    /* ADDRESS BOXES */
                    .address-box { display: flex; justify-content: space-between; margin-bottom: 10px; border-bottom: 1px dashed #ccc; padding-bottom: 10px; }
                    .sender, .receiver { width: 48%; }
                    .box-title { font-size: 8pt; color: #555; text-transform: uppercase; font-weight: bold; margin-bottom: 2px; }
                    .company-name { font-size: 10pt; font-weight: bold; margin-bottom: 2px; }
                    .address-text { font-size: 8pt; line-height: 1.3; color: #333; }
                    
                    /* TABLE */
                    table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
                    th, td { border: 1px solid #999; padding: 4px; text-align: center; font-size: 8.5pt; }
                    th { background-color: #eee !important; font-weight: bold; text-transform: uppercase; }
                    .text-left { text-align: left; padding-left: 8px; }
                    
                    /* PAGE BREAKS */
                    tr { page-break-inside: avoid; }
                    thead { display: table-header-group; }
                    /* REMOVED TFOOT REPETITION BY NOT USING TFOOT TAG FOR TOTALS */
                    
                    /* TOTALS SECTION (OUTSIDE TABLE) */
                    .totals-box { 
                        page-break-inside: avoid; 
                        border: 1px solid #999; 
                        margin-bottom: 10px;
                        font-size: 9pt;
                    }
                    .total-row { display: flex; justify-content: space-between; padding: 4px 10px; border-bottom: 1px solid #eee; }
                    .total-row:last-child { border-bottom: none; background: #f0f0f0; font-weight: 800; border-top: 2px solid #000; }
                    .total-label { width: 60%; text-align: right; padding-right: 15px; font-style: italic; }
                    .total-val { width: 20%; text-align: center; }
                    
                    /* FOOTER & LEGAL */
                    .footer-legal { font-size: 7pt; margin-top: 5px; border-top: 1px solid #ccc; padding-top: 5px; page-break-inside: avoid; }
                    .footer-legal strong { display: block; margin-bottom: 2px; font-size: 8pt; }
                    .footer-legal ol { padding-left: 15px; margin: 0; }
                    .footer-legal li { margin-bottom: 1px; }
                    
                    /* SIGNATURES */
                    .signatures { display: flex; justify-content: space-between; margin-top: 30px; page-break-inside: avoid; }
                    .sign-box { text-align: center; width: 40%; }
                    .sign-line { border-top: 1px solid #000; margin-top: 30px; padding-top: 5px; font-weight: bold; font-size: 9pt; }
                }
            `}
          </style>

          <div className="print-header">
              <div className="print-title">VEKARIYA GEMS</div>
              <div className="print-subtitle">Subject to Surat Jurisdiction</div>
              <div className="print-tag">DISPATCH JANGAD</div>
          </div>

          <div className="address-box">
              <div className="sender">
                  <div className="box-title">FROM</div>
                  <div className="address-text">
                      First Floor, Safal House, Plot no. 22A & 22B,<br/>
                      Mahatma Industrial Co.Op Service Society V-2,<br/>
                      Varachha Road, Surat - 395006.
                  </div>
                  <div style={{fontWeight:'bold', fontSize:'8pt', marginTop:'3px'}}>GSTIN: 24AALFV5724D1Z7</div>
              </div>
              <div className="receiver">
                  <div className="box-title">TO</div>
                  <div className="company-name">{formData.receiverName.toUpperCase()}</div>
                  <div className="address-text">{formData.receiverAddress}</div>
                  {formData.receiverGst && <div style={{fontWeight:'bold', fontSize:'8pt'}}>GSTIN: {formData.receiverGst}</div>}
                  
                  <div style={{marginTop: '5px', borderTop: '1px dotted #999', paddingTop: '3px', display:'flex', justifyContent:'space-between'}}>
                      <span><strong>Date:</strong> {new Date(formData.date).toLocaleDateString('en-GB')}</span>
                      <span><strong>Memo:</strong> {nextMemo}</span>
                  </div>
              </div>
          </div>

          <table>
              <thead>
                  <tr>
                      <th style={{width:'5%'}}>#</th>
                      <th style={{width:'30%'}}>Description</th>
                      <th style={{width:'15%'}}>Jangad No</th>
                      <th style={{width:'10%'}}>Pcs</th>
                      <th style={{width:'10%'}}>Cts</th>
                      <th style={{width:'30%'}}>Process</th>
                  </tr>
              </thead>
              <tbody>
                  {rows.map((row, index) => (
                      <tr key={index}>
                          <td>{index + 1}</td>
                          <td className="text-left">{row.description}</td>
                          <td>{row.jangadNo}</td>
                          <td>{row.pieces}</td>
                          <td>{row.carats}</td>
                          <td className="text-left">{row.process}</td>
                      </tr>
                  ))}
              </tbody>
          </table>

          {/* TOTALS SECTION (MOVED OUTSIDE TABLE) */}
          <div className="totals-box">
              {Object.entries(processBreakdown).map(([procName, data], i) => (
                  <div key={i} className="total-row">
                      <div className="total-label">Total ({procName}):</div>
                      <div className="total-val"><strong>{data.pcs}</strong> Pcs</div>
                      <div className="total-val"><strong>{(data.cts || 0).toFixed(2)}</strong> Cts</div>
                  </div>
              ))}
              <div className="total-row">
                  <div className="total-label" style={{fontStyle:'normal', textTransform:'uppercase'}}>GRAND TOTAL</div>
                  <div className="total-val">{totalPcs}</div>
                  <div className="total-val">{(totalCts || 0).toFixed(2)}</div>
              </div>
          </div>

          <div className="footer-legal">
              <strong>ACKNOWLEDGEMENT OF ENTRUSTMENT</strong>
              <ol>
                  <li>We hereby entrust to you the following goods for Export/Manufacturing/Inspection on approval basis.</li>
                  <li>The goods remain our Property. You will not sell, pledge, or mortgage them.</li>
                  <li>The goods will be returned to us forthwith on demand in the same condition.</li>
              </ol>
          </div>

          <div className="signatures">
              <div className="sign-box">
                  <div style={{fontSize:'8pt', fontStyle:'italic'}}>Received goods as per condition</div>
                  <div className="sign-line">Receiver's Signature</div>
              </div>
              <div className="sign-box">
                  <div style={{fontSize:'8pt', textAlign:'right'}}>For, VEKARIYA GEMS</div>
                  <div className="sign-line">Authorised Signature</div>
              </div>
          </div>
      </div>
    </div>
  );
};

export default DispatchJangad;